import { Component, Input, OnChanges, SimpleChanges, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType, ChartData, ChartOptions, ChartDataset } from 'chart.js';
import { EntityChartDataDto } from '../../../../models/chart.models';

/**
 * Multi-series chart component for displaying multiple data series
 * Useful for cumulative charts with multiple entity types
 */
@Component({
  selector: 'app-multi-series-chart',
  standalone: true,
  imports: [CommonModule, BaseChartDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="chart-container" [style.height.px]="height">
      <div *ngIf="loading" class="chart-loading">
        <div class="loading-spinner"></div>
        <span>Loading chart...</span>
      </div>
      
      <div *ngIf="error" class="chart-error">
        <i class="fas fa-exclamation-triangle text-amber-500 text-2xl mb-2"></i>
        <p>{{ error }}</p>
        <button (click)="onRetry()" class="retry-btn">
          <i class="fas fa-redo mr-1"></i> Retry
        </button>
      </div>
      
      <div *ngIf="!loading && !error && hasData" class="chart-content">
        <canvas baseChart
          [data]="chartData"
          [options]="chartOptions"
          [type]="chartType">
        </canvas>
      </div>
      
      <div *ngIf="!loading && !error && !hasData" class="chart-empty">
        <i class="fas fa-chart-line text-gray-300 text-4xl mb-2"></i>
        <p>No data available</p>
      </div>
    </div>
  `,
  styles: [`
    .chart-container {
      position: relative;
      width: 100%;
      min-height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .chart-content {
      width: 100%;
      height: 100%;
    }
    
    .chart-loading,
    .chart-error,
    .chart-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #6b7280;
    }
    
    .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e5e7eb;
      border-top: 3px solid #10b981;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-bottom: 0.5rem;
    }
    
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    
    .retry-btn {
      margin-top: 0.5rem;
      padding: 0.5rem 1rem;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 0.375rem;
      cursor: pointer;
      transition: background 0.2s;
    }
    
    .retry-btn:hover {
      background: #059669;
    }
  `]
})
export class MultiSeriesChartComponent implements OnChanges {
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  @Input() dataSeries: EntityChartDataDto[] = [];
  @Input() title: string = '';
  @Input() type: 'line' | 'bar' = 'line';
  @Input() colors: string[] = [];
  @Input() height: number = 350;
  @Input() loading: boolean = false;
  @Input() error: string = '';
  @Input() showLegend: boolean = true;
  @Input() stacked: boolean = false;
  @Input() fill: boolean = false;
  @Input() retryCallback?: () => void;

  private defaultColors = [
    '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
  ];

  chartType: ChartType = 'line';
  chartData: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartOptions = {};

  // Cache hasData to avoid recalculation on every change detection
  private _hasData: boolean = false;

  get hasData(): boolean {
    return this._hasData;
  }

  constructor(private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type']) {
      this.chartType = this.type as ChartType;
    }

    if (changes['dataSeries'] || changes['colors'] || changes['fill'] ||
      changes['showLegend'] || changes['stacked']) {
      this.updateChart();
    }
  }

  /**
   * Extract label from data point, handling different API formats
   */
  private getLabel(dp: any): string {
    const rawLabel = dp.label || dp.date || dp.period || dp.timestamp || dp.name || dp.x || '';
    return this.formatLabel(rawLabel);
  }

  /**
   * Extract value from data point, handling different API formats
   */
  private getValue(dp: any): number {
    const value = dp.value ?? dp.count ?? dp.total ?? dp.y ?? dp.amount ?? 0;
    return typeof value === 'number' ? value : parseFloat(value) || 0;
  }

  /**
   * Extract labels and values from data points
   */
  private extractDataPoints(dataPoints: any[]): { labels: string[], values: number[] } {
    const labels: string[] = [];
    const values: number[] = [];

    for (const dp of dataPoints) {
      const label = this.getLabel(dp);
      const value = this.getValue(dp);
      if (label) {
        labels.push(label);
        values.push(value);
      }
    }

    return { labels, values };
  }

  /**
   * Format label for display (handle date strings)
   */
  private formatLabel(label: string): string {
    if (label && label.includes('T') && label.includes('-')) {
      try {
        const date = new Date(label);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric'
          });
        }
      } catch {
        // Not a valid date
      }
    }
    return label;
  }

  private updateChart(): void {
    if (!this.dataSeries?.length) {
      this._hasData = false;
      this.cdr.markForCheck();
      return;
    }

    // Get all unique labels from all series
    const allLabels = new Set<string>();
    this.dataSeries.forEach(series => {
      series.dataPoints?.forEach(dp => {
        const label = this.getLabel(dp);
        if (label) allLabels.add(label);
      });
    });

    const labels = Array.from(allLabels).sort((a, b) => {
      // Try to sort as dates if possible
      const dateA = new Date(a);
      const dateB = new Date(b);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return dateA.getTime() - dateB.getTime();
      }
      return a.localeCompare(b);
    });

    if (labels.length === 0) {
      this._hasData = false;
      this.cdr.markForCheck();
      return;
    }

    // Create datasets for each series
    const datasets: ChartDataset[] = this.dataSeries.map((series, index) => {
      const color = this.colors[index] || this.defaultColors[index % this.defaultColors.length];

      // Map data points to match labels
      const dataMap = new Map<string, number>();
      series.dataPoints?.forEach(dp => {
        const label = this.getLabel(dp);
        const value = this.getValue(dp);
        if (label) dataMap.set(label, value);
      });

      const data = labels.map(label => dataMap.get(label) ?? 0);

      return {
        label: series.entityName || `Series ${index + 1}`,
        data: data,
        backgroundColor: this.fill ? this.hexToRgba(color, 0.3) : color,
        borderColor: color,
        borderWidth: 2,
        fill: this.fill,
        tension: 0.4,
        pointBackgroundColor: color,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: color,
        pointRadius: 4,
        pointHoverRadius: 6
      } as ChartDataset;
    });

    // Calculate hasData
    this._hasData = datasets.some(dataset =>
      Array.isArray(dataset.data) && dataset.data.some((v: any) => typeof v === 'number' && v > 0)
    );

    this.chartData = {
      labels: labels,
      datasets: datasets
    };

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 0 // Disable animation to prevent shaking
      },
      plugins: {
        legend: {
          display: this.showLegend,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: !!this.title,
          text: this.title,
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: {
            bottom: 20
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12
        }
      },
      scales: {
        x: {
          stacked: this.stacked,
          grid: {
            display: false
          },
          ticks: {
            maxTicksLimit: 15, // Limit number of x-axis labels
            maxRotation: 45,
            minRotation: 0,
            autoSkip: true
          }
        },
        y: {
          stacked: this.stacked,
          beginAtZero: true,
          grid: {
            color: '#f3f4f6'
          },
          ticks: {
            precision: 0
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      elements: {
        point: {
          radius: labels.length > 30 ? 0 : 4, // Hide points for large datasets
          hoverRadius: 6
        },
        line: {
          tension: 0.3
        }
      }
    };

    // Trigger change detection and force chart update
    this.cdr.markForCheck();

    // Use requestAnimationFrame for better performance
    requestAnimationFrame(() => {
      this.chart?.update('none');
    });
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  onRetry(): void {
    if (this.retryCallback) {
      this.retryCallback();
    }
  }
}

